import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, UserRound, LogOut, Plus, BookOpen, Bell } from 'lucide-react';
import { LearnAboutPfpCard } from './parentapp/CrossJoinCards';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ParentDashboard({ onLogout }) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchChildren();
    fetchUnreadCount();
  }, []);

  const fetchChildren = async (attempt = 0) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }
      if (attempt < 2 && (!status || status >= 500)) {
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
        return fetchChildren(attempt + 1);
      }
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch notification count');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/parent/notifications">
              <Button variant="ghost" data-testid="notifications-btn" className="rounded-full relative">
                <Bell className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/learn">
              <Button variant="ghost" data-testid="learn-btn" className="rounded-full">
                <BookOpen className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Learn More</span>
              </Button>
            </Link>
            <Link to="/join-with-code">
              <Button variant="ghost" data-testid="join-code-btn" className="rounded-full">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Join with code</span>
              </Button>
            </Link>
            <div className="hidden sm:flex items-center gap-2">
              <UserRound className="w-4 h-4 text-stone-600" />
              <span className="text-sm text-stone-600">{user?.name}</span>
            </div>
            <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-4">
              Welcome back
            </h2>
            <p className="text-xl text-stone-600 leading-relaxed max-w-2xl mx-auto">
              Your child's therapy journey, one connection at a time
            </p>
          </div>

          {/* Cross-join CTA — Clinical parent doesn't have PFP yet */}
          {user?.capabilities && !user.capabilities.pfp && (
            <div className="mb-8">
              <LearnAboutPfpCard />
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading...</p>
            </div>
          ) : children.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <p className="text-xl text-stone-600 mb-2">No children profiles yet</p>
              <p className="text-stone-500">Your therapist will share profiles with you</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {children.map((child, index) => (
                <motion.div
                  key={child.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-8 bg-white rounded-3xl border border-stone-100 hover:shadow-lg transition-all" data-testid={`child-card-${child.id}`}>
                    <div className="text-center space-y-4">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <UserRound className="w-10 h-10 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-serif text-primary-900 mb-2">{child.name}</h3>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to={`/weekly-focus/${child.id}`}>
                          <Button className="rounded-full w-full sm:w-auto" data-testid={`weekly-focus-btn-${child.id}`}>
                            Weekly Focus
                          </Button>
                        </Link>
                        <Link to={`/children/${child.id}`}>
                          <Button variant="outline" className="rounded-full w-full sm:w-auto" data-testid={`view-profile-btn-${child.id}`}>
                            View Profile
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-16 p-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl">
            <p className="font-accent text-2xl text-center text-primary-800 -rotate-1">
              Remember: Every small moment of connection matters more than any specific word or sound
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
